import { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ArrowRight,
  ChevronLeft,
  ChevronRight,
  Shield,
  TrendingDown,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import type { Asset } from '../../services/asset.api';
import { toAbsoluteImageUrl } from '../../services/asset.api';
import { calculateHealthScore } from '../../utils/healthScore';

// ─── Types ───────────────────────────────────────────────────────────────────

type AdType = 'upgrade' | 'protection_plan';

interface AdCard {
  id: string;
  type: AdType;
  badge: string;
  title: string;
  subtitle: string;
  ctaLabel: string;
  route: string;
  icon: React.FC<{ className?: string }>;
  imageUrl?: string;
  imageAlt?: string;
  /** CSS hex or hsl color for accent (badge, button) */
  accentHex: string;
  /** CSS gradient string for card background */
  bgCss: string;
  /** Health score — used for upgrade ads */
  healthScore?: number;
}

function createSvgImage(label: string, accent: string) {
  const svg = `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 520 360">
  <defs>
    <linearGradient id="bg" x1="0" y1="0" x2="1" y2="1">
      <stop offset="0%" stop-color="#F8FAFC" />
      <stop offset="100%" stop-color="#EEF2F7" />
    </linearGradient>
  </defs>
  <rect width="520" height="360" rx="28" fill="url(#bg)" />
  <rect x="40" y="50" width="440" height="220" rx="18" fill="#FFFFFF" stroke="#E5E7EB" />
  <circle cx="110" cy="160" r="36" fill="${accent}" opacity="0.12" />
  <rect x="170" y="120" width="280" height="16" rx="8" fill="#CBD5E1" />
  <rect x="170" y="150" width="220" height="12" rx="6" fill="#E2E8F0" />
  <rect x="170" y="174" width="180" height="12" rx="6" fill="#E2E8F0" />
  <rect x="170" y="210" width="140" height="28" rx="14" fill="${accent}" opacity="0.22" />
  <text x="80" y="300" font-family="Inter, Arial, sans-serif" font-size="22" font-weight="600" fill="#64748B">${label}</text>
</svg>`;
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
}

// ─── Ad builders ─────────────────────────────────────────────────────────────

/** Normalize an asset to a browse-catalog category key. Scans every descriptive
 *  field (category, subcategory, name, title, model) because the top-level
 *  `category` is often a generic value like "Appliances". */
function adCategoryKey(asset: Asset): string {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const a = asset as any;
  const s = [
    a.subCategory, a.productSubtype, a.category, a.productCategory,
    a.type, a.name, a.productTitle, a.model,
  ].filter(Boolean).join(' ').toLowerCase();

  // Order matters: dishwasher before washer (both contain "wash")
  if (s.includes('dishwasher') || s.includes('dish washer')) return 'dishwasher';
  if (s.includes('refrigerator') || s.includes('fridge')) return 'refrigerator';
  if (s.includes('washer') || s.includes('washing') || s.includes('laundry') || s.includes('dryer')) return 'washer';
  if (s.includes('microwave')) return 'microwave';
  if (s.includes('air condition') || s.includes('conditioner') || s.includes('hvac') || /\bac\b/.test(s) || s.includes('mini split')) return 'air conditioner';
  return 'appliances'; // unknown → generic (browse shows all)
}

/** Builds a single upgrade ad for an asset, styled/worded by health tier. */
function makeUpgradeAd(asset: Asset, score: number): AdCard {
  const name = asset.name || asset.productTitle || 'Your appliance';
  const cat = adCategoryKey(asset);
  const isRed = score < 6;
  const isAmber = score >= 6 && score < 7;

  const accentHex = isRed ? '#DC2626' : isAmber ? '#D97706' : '#2563EB';
  const bgCss = isRed
    ? 'linear-gradient(135deg, #FEF2F2 0%, #FEE2E2 100%)'
    : isAmber
    ? 'linear-gradient(135deg, #FFFBEB 0%, #FEF3C7 100%)'
    : 'linear-gradient(135deg, #EFF6FF 0%, #DBEAFE 100%)';

  const subtitle = isRed
    ? `Your ${name} is showing poor health and repair costs are likely to climb. Trade it in now for a newer, more efficient model — with an instant trade-in credit and 0% APR financing to make the switch easy.`
    : isAmber
    ? `Your ${name}'s performance is dipping below average. Upgrade to a newer model with better efficiency, lower running costs, and an instant trade-in credit toward your purchase.`
    : `Get ahead of wear and tear — trade in your ${name} for the latest energy-efficient model. Enjoy lower bills, modern features, an instant trade-in credit, and flexible 0% APR financing.`;

  return {
    id: `upgrade-${asset.id}`,
    type: 'upgrade',
    badge: `HEALTH ${score}/10`,
    title: `Upgrade your ${name}`,
    subtitle,
    ctaLabel: 'Explore Upgrades',
    route: `/assets/${asset.id}/upgrade/browse?category=${encodeURIComponent(cat)}`,
    icon: TrendingDown,
    imageUrl: toAbsoluteImageUrl(asset.productImageUrl ?? asset.imageUrl ?? ''),
    imageAlt: name,
    accentHex,
    bgCss,
    healthScore: score,
  };
}

/** True if the asset's warranty has expired (and no active extended coverage). */
function isWarrantyExpired(asset: Asset): boolean {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const a = asset as any;
  if (a.extendedWarrantyStatus === 'active') return false;
  if (a.warrantyStatus === 'expired') return true;
  if (a.manufacturerWarrantyStatus === 'expired' && a.extendedWarrantyStatus !== 'active') return true;
  const now = Date.now();
  for (const v of [a.warrantyExpiresAt, a.manufacturerWarrantyExpiry, a.extendedWarrantyExpiry, a.warrantyEndDate]) {
    if (v) {
      const t = new Date(v).getTime();
      if (!Number.isNaN(t) && t < now) return true;
    }
  }
  return false;
}

/** True if the asset already has active extended-warranty / protection coverage. */
function isCovered(asset: Asset): boolean {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const a = asset as any;
  return a.extendedWarrantyStatus === 'active' || a.hasActiveProtectionPlan === true;
}

const VIOLET = '#7C3AED';
const VIOLET_BG = 'linear-gradient(135deg, #F5F3FF 0%, #EDE9FE 100%)';
const RED = '#DC2626';
const RED_BG = 'linear-gradient(135deg, #FEF2F2 0%, #FEE2E2 100%)';

/** Builds a single protection-plan ad for an asset (expired = red styling). */
function makeProtectionAd(asset: Asset, expired: boolean): AdCard {
  const name = asset.name || asset.productTitle || 'Your appliance';
  return {
    id: `protection-${asset.id}`,
    type: 'protection_plan',
    badge: expired ? 'WARRANTY EXPIRED' : 'PROTECTION PLAN',
    title: expired ? `${name} — warranty expired` : `Protect your ${name}`,
    subtitle: expired
      ? `The warranty on your ${name} has ended, leaving you exposed to full repair costs. Add a HomeIQ protection plan to cover parts and labor — with no deductibles — from just a few dollars a month.`
      : `Keep your ${name} covered against unexpected breakdowns. A HomeIQ protection plan handles parts, labor, and repairs with no hidden fees, so a surprise failure never turns into a costly bill.`,
    ctaLabel: 'Get Protected',
    route: `/warranty-plans?assetId=${asset.id}`,
    icon: Shield,
    imageUrl: toAbsoluteImageUrl(asset.productImageUrl ?? asset.imageUrl ?? ''),
    imageAlt: name,
    accentHex: expired ? RED : VIOLET,
    bgCss: expired ? RED_BG : VIOLET_BG,
  };
}

function makeGenericProtectionAd(): AdCard {
  return {
    id: 'protection-generic',
    type: 'protection_plan',
    badge: 'PROTECTION PLAN',
    title: 'Keep your home protected',
    subtitle: 'Extend coverage on your appliances with a HomeIQ plan',
    ctaLabel: 'View Plans',
    route: '/warranty-plans',
    icon: Shield,
    accentHex: VIOLET,
    bgCss: VIOLET_BG,
  };
}

/**
 * Master ad builder — guarantees 3–5 personalized ads (per user / per house).
 *
 * Priority passes (each asset used at most once):
 *   A. Expired-warranty assets  → protection ad (red)        [max 2 protection]
 *   B. Low-health assets (< 8)  → upgrade ad (tiered color)  [max 3 upgrade]
 *   C. Fill to reach the target: remaining assets become soft upgrade ads
 *      (fill upgrade to 3 first) then protection ads (fill to 2).
 *   D. If still under 3 (few assets), pad with a generic protection ad.
 *
 * Display order: expired-protection → upgrades → other protection.
 */
function buildAds(assets: Asset[], dismissed: Set<string>): AdCard[] {
  const MAX_UPGRADE = 3;
  const MAX_PROTECTION = 2;
  const MIN_TOTAL = 3;
  const MAX_TOTAL = 5;

  // No assets in the house → no ads at all (ads appear once an asset is added).
  if (assets.length === 0) {
    return [];
  }

  const MIN_PROTECTION = 1; // always surface at least one protection (purple/red) ad

  const byHealth = [...assets].sort((a, b) => calculateHealthScore(a) - calculateHealthScore(b));
  const used = new Set<string>();
  const expiredProtection: AdCard[] = [];
  const upgradeAds: AdCard[] = [];
  const normalProtection: AdCard[] = [];

  const protectionCount = () => expiredProtection.length + normalProtection.length;
  const total = () => expiredProtection.length + upgradeAds.length + normalProtection.length;

  // Pass A — expired warranty → protection (red)
  for (const a of byHealth) {
    if (protectionCount() >= MAX_PROTECTION) break;
    if (used.has(a.id) || dismissed.has(`protection-${a.id}`)) continue;
    if (!isWarrantyExpired(a)) continue;
    expiredProtection.push(makeProtectionAd(a, true));
    used.add(a.id);
  }

  // Pass B — low health (< 8) → upgrade (tiered color)
  for (const a of byHealth) {
    if (upgradeAds.length >= MAX_UPGRADE) break;
    if (used.has(a.id) || dismissed.has(`upgrade-${a.id}`)) continue;
    const score = calculateHealthScore(a);
    if (score >= 8) continue;
    upgradeAds.push(makeUpgradeAd(a, score));
    used.add(a.id);
  }

  // Pass C — normal protection (violet) for uncovered assets: run BEFORE filling
  // upgrades so a purple protection ad is guaranteed a slot.
  for (const a of byHealth) {
    if (protectionCount() >= MAX_PROTECTION) break;
    if (used.has(a.id) || dismissed.has(`protection-${a.id}`)) continue;
    if (isCovered(a)) continue;
    normalProtection.push(makeProtectionAd(a, false));
    used.add(a.id);
  }

  // Pass D — fill remaining slots with soft upgrade ads
  for (const a of byHealth) {
    if (total() >= MAX_TOTAL) break;
    if (upgradeAds.length >= MAX_UPGRADE) break;
    if (used.has(a.id) || dismissed.has(`upgrade-${a.id}`)) continue;
    upgradeAds.push(makeUpgradeAd(a, calculateHealthScore(a)));
    used.add(a.id);
  }

  const combined = [...expiredProtection, ...upgradeAds, ...normalProtection];

  // Pass E — guarantee at least one protection ad (generic violet fallback when
  // every asset is already covered / expired ads got pushed out).
  if (protectionCount() < MIN_PROTECTION && !dismissed.has('protection-generic')) {
    combined.push(makeGenericProtectionAd());
  }

  // Pass F — pad to the minimum total if still short
  if (
    combined.length < MIN_TOTAL &&
    !dismissed.has('protection-generic') &&
    !combined.some((c) => c.id === 'protection-generic')
  ) {
    combined.push(makeGenericProtectionAd());
  }

  return combined.slice(0, MAX_TOTAL);
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function readDismissed(): Set<string> {
  try {
    const raw = localStorage.getItem('promo_ads_dismissed');
    return new Set(raw ? (JSON.parse(raw) as string[]) : []);
  } catch {
    return new Set();
  }
}

function saveDismissed(ids: Set<string>) {
  try {
    localStorage.setItem('promo_ads_dismissed', JSON.stringify([...ids]));
  } catch { /* ignore */ }
}

// ─── Component ────────────────────────────────────────────────────────────────

interface PromoAdsCarouselProps {
  assets: Asset[];
}

export function PromoAdsCarousel({ assets }: PromoAdsCarouselProps) {
  const navigate = useNavigate();
  const { t: tCommon } = useTranslation('common');
  const { t: tDashboard } = useTranslation('dashboard');
  const [dismissed, setDismissed] = useState<Set<string>>(readDismissed);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  // Build 3–5 personalized ads (expired → upgrade → protection, with fill)
  const allCards = useMemo<AdCard[]>(() => buildAds(assets, dismissed), [assets, dismissed]);

  // Show up to 5 ads
  const visibleCards = useMemo(() => allCards.slice(0, 5), [allCards]);

  const safeVisibleCards = visibleCards;

  const handleDismiss = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    const next = new Set(dismissed);
    next.add(id);
    setDismissed(next);
    saveDismissed(next);
  };

  const handleClick = (card: AdCard) => navigate(card.route);

  // Reset index if out of bounds
  useEffect(() => {
    if (currentIndex >= safeVisibleCards.length) setCurrentIndex(0);
  }, [currentIndex, safeVisibleCards.length]);

  // Auto-advance every 6 seconds
  useEffect(() => {
    if (safeVisibleCards.length <= 1 || isPaused) return;
    const id = window.setInterval(() => {
      setCurrentIndex(i => (i + 1) % safeVisibleCards.length);
    }, 6000);
    return () => window.clearInterval(id);
  }, [isPaused, safeVisibleCards.length]);

  const canNavigate = safeVisibleCards.length > 1;
  const nextSlide = () => setCurrentIndex(i => (i + 1) % safeVisibleCards.length);
  const prevSlide = () => setCurrentIndex(i => (i - 1 + safeVisibleCards.length) % safeVisibleCards.length);

  // No ads (e.g. brand-new user with no assets) → hide the whole section.
  if (safeVisibleCards.length === 0) return null;

  return (
    <section className="mx-auto max-w-7xl px-6 lg:px-8 py-6">
      {/* Section header */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="text-[11px] font-bold text-zinc-500 uppercase tracking-[0.12em] mb-1">
            {tDashboard('promotions.recommendedForYou')}
          </h2>
          {canNavigate && (
            <p className="text-[13px] font-medium text-zinc-400">
              {currentIndex + 1} of {safeVisibleCards.length}
            </p>
          )}
        </div>
      </div>

      {/* Carousel */}
      <div
        className="relative"
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
      >
        {/* Navigation arrows */}
        {canNavigate && (
          <>
            <button
              type="button"
              onClick={prevSlide}
              className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-5 z-20
                h-9 w-9 rounded-full bg-white shadow-lg border border-zinc-200
                flex items-center justify-center text-zinc-600 hover:text-zinc-900 hover:shadow-xl
                transition-all duration-200 hover:scale-110 active:scale-95"
              aria-label={tCommon('a11y.scrollLeft')}
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              type="button"
              onClick={nextSlide}
              className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-5 z-20
                h-9 w-9 rounded-full bg-white shadow-lg border border-zinc-200
                flex items-center justify-center text-zinc-600 hover:text-zinc-900 hover:shadow-xl
                transition-all duration-200 hover:scale-110 active:scale-95"
              aria-label={tCommon('a11y.scrollRight')}
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </>
        )}

        {/* Slide track */}
        <div className="overflow-hidden rounded-2xl bg-white border border-zinc-200 shadow-md">
          <div
            className="flex transition-transform duration-700 ease-out"
            style={{ transform: `translateX(-${currentIndex * 100}%)` }}
            aria-label={tDashboard('a11y.promoCarousel')}
          >
            {safeVisibleCards.map((card) => {
              const accent = card.accentHex;
              const badgeBg = `${accent}1A`; // ~10% opacity
              const imageSrc = card.imageUrl || createSvgImage(card.title.split(' ').slice(0, 2).join(' '), accent);

              return (
                <div key={card.id} className="flex-shrink-0 w-full">
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => handleClick(card)}
                    onKeyDown={e => e.key === 'Enter' && handleClick(card)}
                    className="group flex flex-row items-stretch h-[210px] cursor-pointer select-none overflow-hidden transition-all duration-300 hover:shadow-lg"
                    style={{ background: card.bgCss }}
                    aria-label={card.title}
                  >
                    {/* ── Left: Text content ─────────────────────────── */}
                    <div className="flex flex-col justify-center px-8 py-5 flex-1 min-w-0">
                      {/* Badge */}
                      <div className="flex items-center gap-3 mb-2">
                        <span
                          className="inline-flex items-center px-3.5 py-1.5 rounded-full text-[10px] font-black tracking-[0.15em] uppercase shadow-sm"
                          style={{ background: badgeBg, color: accent, border: `1.5px solid ${accent}30` }}
                        >
                          <span className="mr-1.5" style={{ fontSize: '8px' }}>●</span>
                          {card.badge}
                        </span>
                      </div>

                      {/* Title (header) */}
                      <h3 className="text-[20px] font-bold text-zinc-900 leading-tight mb-2 line-clamp-1">
                        {card.title}
                      </h3>

                      {/* Promotional description */}
                      <p className="text-[13.5px] text-zinc-600 leading-relaxed mb-4 line-clamp-2">
                        {card.subtitle}
                      </p>

                      {/* CTA row */}
                      <div className="flex items-center gap-4">
                        <button
                          onClick={e => { e.stopPropagation(); handleClick(card); }}
                          className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-[13px] font-bold text-white shadow-lg hover:shadow-xl transition-all duration-200 hover:-translate-y-0.5 active:scale-95"
                          style={{ background: accent, boxShadow: `0 4px 12px ${accent}30, 0 1px 3px rgba(0,0,0,0.1)` }}
                        >
                          {card.ctaLabel}
                          <ArrowRight className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={e => handleDismiss(e, card.id)}
                          className="text-[12px] text-zinc-400 hover:text-zinc-700 font-medium transition-colors"
                          aria-label={tCommon('a11y.dismiss')}
                        >
                          Dismiss
                        </button>
                      </div>
                    </div>

                    {/* ── Right: Product image (centered square) ──────── */}
                    <div className="w-[220px] shrink-0 flex items-center justify-center p-4">
                      <div className="h-[150px] w-[150px] rounded-2xl bg-white shadow-sm ring-1 ring-black/5 overflow-hidden flex items-center justify-center p-3">
                        <img
                          src={imageSrc}
                          alt={card.imageAlt || card.title}
                          className="max-h-full max-w-full object-contain transition-transform duration-500 group-hover:scale-105"
                          loading="lazy"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Pagination dots */}
        {canNavigate && (
          <div className="flex items-center justify-center gap-2 mt-5">
            {safeVisibleCards.map((card, idx) => (
              <button
                key={card.id}
                type="button"
                onClick={() => setCurrentIndex(idx)}
                className="transition-all duration-300 hover:scale-125"
                style={{
                  height: '9px',
                  width: idx === currentIndex ? '36px' : '9px',
                  borderRadius: '999px',
                  background: idx === currentIndex ? safeVisibleCards[currentIndex].accentHex : '#D4D4D8',
                  boxShadow: idx === currentIndex ? `0 0 12px ${safeVisibleCards[currentIndex].accentHex}50` : 'none',
                }}
                aria-label={`Go to slide ${idx + 1}`}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
